import { Box, Card, CardHeader } from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { TextBubble } from "components/common";
import CustomToolbar from "components/common/CustomToolbar";
import GemDrawerMenu from "components/common/DrawerMenu";
import { useChargingAlerts, useUser, useQuery } from "contexts";
import { formatDistanceToNow } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { export_charging_alerts } from "queries/ChargingQueries";

// --- CSV conversion utility (same as ExportAlertsButton.js) ---
function csvEscape(v) {
  const s = v == null ? "" : String(v).replace(/\r?\n/g, " ");
  return `"${s.replace(/"/g, '""')}"`;
}

function rowsToCSV(rows) {
  const header = [
    "Priority", "Display Name", "Status", "Text", "Occurred At",
    "Vehicle Display Name"
    // Add other fields as needed
  ];
  const lines = [header.join(",")];

  for (const r of rows) {
    lines.push([
      csvEscape(r.priority),
      csvEscape(r.displayName),
      csvEscape(r.status),
      csvEscape(r.text),
      csvEscape(r.occuredAt),
      csvEscape(r.vehicle?.displayName),
    ].join(","));
  }
  return lines.join("\r\n");
}
// -------------------------------------------------------------

function ChargingAlertsPage() {
  const { context: user_context, register_user } = useUser();
  const { user } = user_context;
  useEffect(() => {
    register_user();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const { dispatch, alerts } = useChargingAlerts({ refetchInterval: 40000 });

  function formatDistanceDay(date) {
    return formatDistanceToNow(date, { addSuffix: true, includeSeconds: true }).replace('about ', '').replace('less than a', '1').replace('less than', '');
  }

  const nameAdminSortComparator = (v1, v2, param1, param2) => {
    const priorityOrder = ['Critical', 'High', 'Medium', 'Low', 'Expired'];
    if (priorityOrder.indexOf(v1.priority) === -1 || priorityOrder.indexOf(v2.priority) === -1) {
      return 0;
    } else if (v1.priority != v2.priority) {
      return priorityOrder.indexOf(v1.priority) > priorityOrder.indexOf(v2.priority) ? -1 : 1;
    }
    if (new Date(v1.occurredAt) === new Date(v2.occurredAt)) {
      return 0;
    }
    if (new Date(v1.occurredAt) === null || new Date(v2.occurredAt) === null) {
      return 0;
    }
    return new Date(v1.occurredAt) > new Date(v2.occurredAt);
  };

  const rows = alerts.alerts;
  const columns = [
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      valueGetter: (value, row) => ({
        priority: row.priority,
        occurredAt: row.occuredAt,
      }),
      valueFormatter: (value) => {
        return value.priority;
      },
      sortComparator: nameAdminSortComparator,
    },
    { field: 'displayName', headerName: 'Type', width: 240 },
    { field: 'text', headerName: 'Description', width: 900 },
    {
      field: 'vehicle', headerName: 'Vehicle', width: 100, valueGetter: (params) => {
        return params?.displayName;
      },
      renderCell: (params) => {
        if (params.value) {
          return (
            <a href={`vehicle/charging/${params.row.vehicle.id}`}>
              {params.value}
            </a>
          );
        }
      }
    },
    {
      field: 'occuredAt', headerName: 'Occurred', width: 120, valueGetter: (params) => {
        const date = Date.parse(params);
        return new Date(date).toLocaleTimeString('en-US', { hour12: true });
      }
    },
    {
      field: 'timeElapsed', headerName: 'Time Elapsed', width: 120, valueGetter: (params, row) => {
        return formatDistanceDay(Date.parse(row.occuredAt));
      },
      renderCell: (params) => {
        if (new Date() - Date.parse(params.row.occuredAt) < 30 * 1000) {
          return (
            <div>
              {params.value} <span style={{ marginRight: "5px" }}><TextBubble colour="#4CAF50" /></span>
            </div>
          );
        }
        return params.value;
      },
    },
  ];

  const sortModel = [
    {
      field: 'priority',
      sort: 'desc',
    },
  ];

  const getRowClassName = (params) => {
    if (params.row.status === 'Expired') {
      return 'expired-light';
    }
    switch (params.row.priority) {
      case 'Critical':
        return 'critical-light';
      case 'High':
        return 'critical-light';
      case 'Medium':
        return 'warning-light';
      case 'Low':
        return 'info-light';
      case 'Expired':
        return 'expired-light';
      default:
        return '';
    }
  };

  // Add exporting state
  const [exporting, setExporting] = useState(false);

  // --- CSV Export Logic using useQuery ---
  const { data, loading, error, refetchData } = useQuery(export_charging_alerts, { enable: false });
  const exportParamsRef = useRef({});

  // Handler for CSV export (passed to CustomToolbar)
  function handleExportChargingCSV({ startLocal, endLocal }) {
    setExporting(true);
    // Always update the ref with the latest params
    exportParamsRef.current = { startLocal, endLocal };
    refetchData(
      {
        size: 10000,
        filter: {
          createdBetween: {
            from: new Date(startLocal).toISOString(),
            to: new Date(endLocal).toISOString()
          }
        }
      },
      true
    );
  }

  // Effect to trigger CSV download when data is loaded or handle error
  useEffect(() => {
    if (error && exporting) {
      setExporting(false);
    }
  }, [error, exporting]);

  useEffect(() => {
    // Always use the latest params from the ref
    const { startLocal, endLocal } = exportParamsRef.current;
    if (!data || !data.exportChargingAlerts || !startLocal || !endLocal) return;
    const blob = new Blob([rowsToCSV(data.exportChargingAlerts)], { type: "text/csv;charset=utf-8;" });
    const d0 = startLocal.replace(/[:T]/g, "-").slice(0, 16) || "";
    const d1 = endLocal.replace(/[:T]/g, "-").slice(0, 16) || "";
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = `charging_alerts_${d0}_to_${d1}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(a.href);
    setExporting(false); // <-- Re-enable button after download
  }, [data]);
  // ---------------------------------------

  // TODO: fix bug where switching quickly between map and alerts page causes non location alerts to be filtered out

  return (
    <Box display="flex" width="100%">
      <GemDrawerMenu />
      <Box sx={{ flexGrow: 1, padding: '5px' }}>
        <Card>
          <Box sx={{ backgroundColor: '#07A0C3', width: '100%', padding: '0px' }}>
            <CardHeader title="Alerts" sx={{ height: '60px' }} />
          </Box>
          <DataGrid
            initialState={{ sortModel: [{ field: 'occuredAt', sort: 'desc' }], density: 'compact' }}
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            getRowClassName={getRowClassName}
            sortModel={sortModel}
            slots={{
              toolbar: (props) => (
                <CustomToolbar
                  {...props}
                  onExportCSV={handleExportChargingCSV}
                  exporting={exporting}
                  exportError={error}
                />
              )
            }}
          />
        </Card>
      </Box>
    </Box>
  );
}

export default ChargingAlertsPage;
